import HTMLFlipBook from "react-pageflip";
import { useParams } from "react-router-dom";
import { useState, useRef, useEffect, useMemo } from "react";
import MagazineImages from "../data/EPurashreeBooks.json";
import { mainURL } from "../utils/common";

export default function FlipBookViewer() {
  const { year, month, issue } = useParams();

  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  const flipRef = useRef(null);

  const book = useMemo(
    () =>
      MagazineImages.find(
        (x) =>
          x.year === Number(year) &&
          x.month === Number(month) &&
          x.issue === issue
      ),
    [year, month, issue]
  );

  const pages = useMemo(() => {
    if (!book) return [];

    const magazine = MagazineImages.find(
      (item) =>
        item.year === Number(year) &&
        item.month === Number(month) &&
        item.issue === issue
    );

    if (!magazine) return [];

    return magazine.fileStoreIds.map(
      (id) =>
        `${mainURL}/filestore/v1/files/id?fileStoreId=${id}&tenantId=km.kolkata`
    );
  }, [year, month, issue, book]);

  useEffect(() => {
    if (!pages.length) return;

    setLoading(true);

    const img = new Image();
    img.src = pages[0];

    img.onload = () => setLoading(false);
    img.onerror = () => setLoading(false);
  }, [pages]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (!e.ctrlKey) return;

      e.preventDefault();

      setZoom((prev) =>
        Math.max(0.7, Math.min(2, prev + (e.deltaY > 0 ? -0.1 : 0.1)))
      );
    };

    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, []);

  if (!book) return <h3 className="text-center mt-5">Book not found</h3>;

  const handleFlip = (e) => {
    setCurrentPage(e.data);
  };

  const goPrev = () => {
    flipRef.current?.pageFlip().flipPrev();
  };

  const goNext = () => {
    flipRef.current?.pageFlip().flipNext();
  };

  const zoomIn = () => setZoom((z) => Math.min(z + 0.1, 2));

  const zoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.7));

  const resetZoom = () => setZoom(1);

  let displayPage;
  const totalPages = pages.length;

  if (currentPage === 0) {
    displayPage = `Page 1 of ${totalPages}`;
  } else {
    const leftPage = currentPage + 1;
    const rightPage = currentPage + 2;

    displayPage =
      rightPage <= totalPages
        ? `Page ${leftPage}–${rightPage} of ${totalPages}`
        : `Page ${leftPage} of ${totalPages}`;
  }

  return (
    <div className="container" style={{ marginTop: "-77px" }}>
      <h5 className="text-center mb-2">{book.title}</h5>

      <div className="zoom-float-controls">
        <button onClick={zoomIn}>+</button>

        <button onClick={resetZoom}>{Math.round(zoom * 100)}%</button>

        <button onClick={zoomOut}>−</button>
      </div>

      <div className="book-toolbar">
        <button
          className="btn btn-primary"
          onClick={goPrev}
          disabled={currentPage === 0}
          style={{
            backgroundColor: "#302ba0",
            borderColor: "#302ba0",
          }}
        >
          Prev
        </button>

        <span className="page-info">{displayPage}</span>

        <button
          className="btn btn-primary"
          onClick={goNext}
          disabled={currentPage >= totalPages - 1}
          style={{
            backgroundColor: "#302ba0",
            borderColor: "#302ba0",
          }}
        >
          Next
        </button>
      </div>

      <div
        className="zoom-wrapper"
        style={{
          transform: `scale(${zoom})`,
          transformOrigin: "center",
          transition: "transform .2s ease",
        }}
      >
        {loading ? (
          <div className="d-flex align-items-center justify-content-center flex-column">
            <div className="spinner"></div>
            <p>Loading Magazine...</p>
          </div>
        ) : (
          <HTMLFlipBook
            width={500}
            height={650}
            showCover
            drawShadow
            mobileScrollSupport
            ref={flipRef}
            onFlip={handleFlip}
          >
            {pages.map((page, index) => (
              <div key={index}>
                <img
                  src={page}
                  alt={`Page ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            ))}
          </HTMLFlipBook>
        )}
      </div>
    </div>
  );
}
